import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getBackgroundJob } from "@/api/background-job/getBackgroundJob";
import { getBackgroundJobKindList } from "@/api/background-job/getBackgroundJobKindList";
import { isBackgroundJobActive } from "@/api/background-job/_models/BackgroundJob";
import { DetailDrawer, useDetailStackDrawer, useDetailStackId } from "@/components/detail-stack";
import { ListGridExpandedFields } from "@/components/list-grid/ListGridExpandedFields";
import { ListGridExpandedSection } from "@/components/list-grid/ListGridExpandedSection";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { DETAIL_KIND } from "@/features/detail/detailKind";
import type { DetailKind } from "@/features/detail/detailKind";
import type { JobRunPrefill } from "@/features/job/job-run/jobRun.types";
import { JobStatusChip } from "@/features/job/JobStatusChip";
import { toJobDuration, toJobFileNameList, toJobKindLabel } from "@/features/job/job.utils";
import { JobListDownloadButton } from "@/features/job/job-list/JobListDownloadButton";
import { JobDetailJson } from "./JobDetailJson";
import { JobDetailStatus } from "./JobDetailStatus";
import { toJobInputFieldList, toJobRunFieldList } from "./jobDetail.utils";

const POLL_MS = 3000;

/**
 * One job run in a wide detail-stack drawer (`jobs/<id>`): its name, what it ran for and its
 * status; where it stands; its files to download; how it ran and its inputs; and the raw input
 * and result. Re-read every few seconds while it's queued or running. "Run again" opens the run
 * drawer on top, filled in with this job's kind and input.
 */
export const JobDetail = () => {
  const level = useDetailStackDrawer<DetailKind>();
  const navigate = useNavigate();
  const jobId = useDetailStackId();
  const query = getBackgroundJob.useQuery(jobId);
  const job = query.data;
  const isActive = isBackgroundJobActive(job);
  getBackgroundJob.useQuerySubscription(jobId, { pollingInterval: isActive ? POLL_MS : 0 });
  const kindsQuery = getBackgroundJobKindList.useQuery();
  const kinds = kindsQuery.data ?? [];
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isActive]);

  let titleText = "Job";
  if (job) titleText = toJobKindLabel(kinds, job.kind);
  if (query.isError) titleText = "Job not found";

  const handleRunAgain = () => {
    if (!job) return;
    const jobRun: JobRunPrefill = { kind: job.kind, input: job.input };
    navigate(level.toChild(DETAIL_KIND.run), { state: { jobRun } });
  };

  const titleBlock = (
    <Stack spacing={0.5}>
      <Typography
        sx={{ fontWeight: 700 }}
        variant="h2"
      >
        {titleText}
      </Typography>
      {job && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          {job.subject && (
            <Typography
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
              variant="body2"
            >
              {job.subject}
            </Typography>
          )}
          <JobStatusChip status={job.status} />
        </Stack>
      )}
    </Stack>
  );

  const title = (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: "flex-start" }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        {titleBlock}
      </Box>
      {job && (
        <Button
          onClick={handleRunAgain}
          size="small"
          variant="outlined"
        >
          Run again
        </Button>
      )}
    </Stack>
  );

  if (!job) {
    return (
      <DetailDrawer title={title}>
        {query.isError && (
          <Typography color="error">
            {toErrorText(query.error)}
          </Typography>
        )}
      </DetailDrawer>
    );
  }

  const kind = kinds.find((entry) => entry.kind === job.kind);
  const fileNames = toJobFileNameList(job);
  const runFields = toJobRunFieldList(job, toJobDuration(job, now));
  const inputFields = toJobInputFieldList(job, kind);

  return (
    <DetailDrawer title={title}>
      <Stack spacing={3}>
        <JobDetailStatus job={job} />
        {fileNames.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap" }}
            useFlexGap
          >
            {fileNames.map((fileName) => (
              <JobListDownloadButton
                fileName={fileName}
                jobId={job.id}
                key={fileName}
              />
            ))}
          </Stack>
        )}
        <Box
          sx={{
            columnGap: 6,
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
            rowGap: 3,
          }}
        >
          <ListGridExpandedSection title="Run">
            <ListGridExpandedFields fields={runFields} />
          </ListGridExpandedSection>
          <ListGridExpandedSection
            emptyText="No inputs"
            isEmpty={inputFields.length === 0}
            title="Inputs"
          >
            <ListGridExpandedFields fields={inputFields} />
          </ListGridExpandedSection>
        </Box>
        {!isActive && (
          <JobDetailJson
            input={job.input}
            result={job.result}
          />
        )}
      </Stack>
    </DetailDrawer>
  );
};
